import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Plus, Share2, Pencil, Trash2, FileText, ClipboardList, Folder, X, Users as UsersIcon, User as UserIcon } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import Skeleton from 'components/ui/skeleton';
import { formatRelativeTime } from 'lib/relativeTime';
import * as CollectionsModule from 'modules/collections';
import * as AuthModule from 'modules/auth';
import { usePermission } from 'modules/auth/hooks';
import { CollectionFormModal } from 'pages/Library/CollectionFormModal';
import { ShareCollectionModal } from './ShareCollectionModal';
import { AddItemsModal } from './AddItemsModal';
import { PERMISSIONS } from 'modules/auth/permissions';
import { useIsMobile } from 'hooks/use-media-query';
import { toPlainText } from 'modules/editor';
import { CollectionDetailMobile } from 'components/mobile/screens/CollectionDetailMobile';

const CollectionDetailDesktop: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { state: { user } } = AuthModule.Context.useAuthContext();
  // Items open the test settings page (TESTS_EDIT). Users who lack that
  // permission — candidates and other viewers — see the list read-only.
  const canOpenItem = usePermission(PERMISSIONS.TESTS_EDIT);

  const { collection, isLoading, isError, error, refetch } = CollectionsModule.Hooks.useSingle({
    id: id || '',
    enabled: Boolean(id),
  });
  const removeItem = CollectionsModule.Hooks.useRemoveItem();
  const deleteCollection = CollectionsModule.Hooks.useDelete();

  const [editOpen, setEditOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="p-6 space-y-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between gap-3">
          <Skeleton className="h-9 w-40" />
          <Skeleton className="h-9 w-24" />
        </div>
        <Skeleton className="h-28 rounded-lg" />
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  // A network/server error is not the same thing as "no such collection" —
  // conflating the two previously sent a genuine fetch failure to the
  // misleading "not found" screen with no way to retry.
  if (isError) {
    return (
      <div className="p-6 text-center">
        <h2 className="text-lg font-medium">{t('failed_to_load')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {error instanceof Error && error.message ? error.message : t('something_went_wrong')}
        </p>
        <div className="mt-4 flex justify-center gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            {t('retry')}
          </Button>
          <Button variant="outline" onClick={() => navigate('/app/library')}>
            {t('collections.back_to_library')}
          </Button>
        </div>
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="p-6 text-center">
        <h2 className="text-lg font-medium">{t('collections.not_found')}</h2>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/app/library')}>
          {t('collections.back_to_library')}
        </Button>
      </div>
    );
  }

  const items = collection.items ?? [];
  const sharedWith = collection.sharedWith ?? [];
  const existingTestIds = items.filter((i) => i.type === 'test').map((i) => i.itemId);

  // Owner-only actions (delete, share). Edit-collaborators can edit metadata and
  // items; `canEdit` comes from the backend (owner or edit-level collaborator).
  const isOwner = Boolean(user?.id) && collection.ownerId === user?.id;
  const canEdit = isOwner || collection.canEdit === true;

  const handleRemoveItem = (type: 'test' | 'question', itemId: string) => {
    if (!id) return;
    removeItem.mutate({ collectionId: id, type, itemId });
  };

  const handleDelete = () => {
    if (!id) return;
    if (window.confirm(t('collections.delete_confirm'))) {
      deleteCollection.mutate(id, { onSuccess: () => navigate('/app/library') });
    }
  };

  const updatedLabel = formatRelativeTime(collection.updatedAt, i18n.language);

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
      {/* Back + actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="outline" size="sm" onClick={() => navigate('/app/library')}>
          <ArrowLeft className="mr-1.5 h-4 w-4" /> {t('collections.back_to_library')}
        </Button>
        <div className="flex flex-wrap gap-2">
          {canEdit && (
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <Plus className="w-4 h-4 mr-2" /> {t('collections.add_items')}
            </Button>
          )}
          {isOwner && (
            <Button variant="outline" size="sm" onClick={() => setShareOpen(true)}>
              <Share2 className="w-4 h-4 mr-2" /> {t('collections.share')}
            </Button>
          )}
          {canEdit && (
            <Button variant="outline" size="icon" onClick={() => setEditOpen(true)} aria-label={t('collections.edit_collection')}>
              <Pencil className="w-4 h-4" />
            </Button>
          )}
          {isOwner && (
            <Button variant="outline" size="icon" className="text-destructive" onClick={handleDelete} aria-label={t('collections.delete_collection')}>
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Identity header */}
      <div className="rounded-lg border border-border bg-card p-5 shadow-sm sm:p-6">
        <div className="flex items-center gap-4">
          <span
            className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-lg bg-muted text-foreground/80"
            aria-hidden
          >
            <Folder className="h-6 w-6" />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-xl font-bold tracking-tight text-foreground sm:text-2xl">{collection.name}</h1>
            {collection.description && (
              <p className="mt-0.5 line-clamp-1 text-sm text-muted-foreground">{collection.description}</p>
            )}
            <p className="mt-1 text-xs text-muted-foreground sm:text-sm">
              {t('collections.items_count', { count: items.length })}
              {updatedLabel && <> · {t('collections.updated_ago', { when: updatedLabel })}</>}
            </p>
          </div>
        </div>
      </div>

      {/* Shared-with chips */}
      {sharedWith.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">{t('collections.shared_with')}</span>
          {sharedWith.map((s) => (
            <Badge key={`${s.type}-${s.targetId}`} variant="secondary" className="gap-1">
              {s.type === 'team' ? <UsersIcon className="h-3 w-3" /> : <UserIcon className="h-3 w-3" />}
              {s.target?.name ?? (s.type === 'team' ? t('collections.team') : t('collections.user'))}
              <span className="text-muted-foreground">· {s.access === 'edit' ? t('collections.can_edit') : t('collections.can_view')}</span>
            </Badge>
          ))}
        </div>
      )}

      {/* Items */}
      {items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <h3 className="text-lg font-medium">{t('collections.no_items_title')}</h3>
            <p className="text-sm text-muted-foreground mt-1">{t('collections.no_items_desc')}</p>
            {canEdit && (
              <Button className="mt-4" onClick={() => setAddOpen(true)}>
                <Plus className="w-4 h-4 mr-2" /> {t('collections.add_items')}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((item) => {
            const clickable = canOpenItem && item.type === 'test';
            const isTest = item.type === 'test';
            const ItemIcon = isTest ? ClipboardList : FileText;
            const body = (
              <>
                {/* Neutral for both kinds: the icon and the type line say which is which. */}
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                  <ItemIcon className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">
                    {item.item?.title || toPlainText(item.item?.prompt) || t('collections.untitled')}
                  </p>
                  <p className="text-xs text-muted-foreground capitalize">{item.type}</p>
                </div>
              </>
            );
            return (
              <Card key={`${item.type}-${item.itemId}`} className={clickable ? 'hover:shadow-sm transition-shadow' : ''}>
                <CardContent className="flex items-center justify-between gap-3 p-3">
                  {clickable ? (
                    <button
                      type="button"
                      className="flex items-center gap-3 min-w-0 text-left"
                      onClick={() => navigate(`/app/tests/${item.itemId}`)}
                    >
                      {body}
                    </button>
                  ) : (
                    <div className="flex items-center gap-3 min-w-0">
                      {body}
                    </div>
                  )}
                  {canEdit && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-destructive shrink-0"
                      onClick={() => handleRemoveItem(item.type, item.itemId)}
                      aria-label={t('collections.remove_from_collection', 'Remove from collection')}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <CollectionFormModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        collection={{ id: collection.id, name: collection.name, description: collection.description }}
        onSuccess={() => setEditOpen(false)}
      />
      <ShareCollectionModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        collectionId={collection.id}
        currentShares={sharedWith}
        onChanged={() => undefined}
      />
      <AddItemsModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        collectionId={collection.id}
        existingTestIds={existingTestIds}
        onAdded={() => undefined}
      />
    </div>
  );
};

export const CollectionDetailPage: React.FC = () =>
  useIsMobile() ? <CollectionDetailMobile /> : <CollectionDetailDesktop />;

export default CollectionDetailPage;
